import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  FileSpreadsheet, Building2, User, Check, Send, PlusCircle, Trash2, Sparkles,
} from "lucide-react";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const emptyProposta = () => ({
  preco: "",
  frete: "CIF",
  forma_pgto: { tipo: "avista", parcelas: [] },
  observacoes: "",
});

const brl = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (isNaN(n)) return "";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

export default function CotacaoLojaFornecedorPage() {
  const { token } = useParams();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Estado local por produto_id
  const [propostas, setPropostas] = useState({});
  const [savingItem, setSavingItem] = useState({});
  const [sending, setSending] = useState(false);
  const [thankyou, setThankyou] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/cotacao/${token}`)
      .then(({ data }) => {
        setDados(data);
        setThankyou(!!data.respondida);
        // pré-preenche com propostas existentes
        const inicial = {};
        (data.itens || []).forEach((it) => {
          if (it.proposta) {
            inicial[it.produto_id] = {
              preco: it.proposta.preco ?? "",
              frete: it.proposta.frete || "CIF",
              forma_pgto: it.proposta.forma_pgto || { tipo: "avista", parcelas: [] },
              observacoes: it.proposta.observacoes || "",
              saved: true,
            };
          } else {
            inicial[it.produto_id] = { ...emptyProposta(), saved: false };
          }
        });
        setPropostas(inicial);
      })
      .catch((e) => setErro(e?.response?.data?.detail || "Link inválido ou expirado"))
      .finally(() => setLoading(false));
  }, [token]);

  const setField = (pid, field, value) => {
    setPropostas((s) => ({
      ...s,
      [pid]: { ...(s[pid] || emptyProposta()), [field]: value, saved: false },
    }));
  };
  const setForma = (pid, patch) => {
    setPropostas((s) => ({
      ...s,
      [pid]: {
        ...(s[pid] || emptyProposta()),
        forma_pgto: { ...(s[pid]?.forma_pgto || {}), ...patch },
        saved: false,
      },
    }));
  };
  const addParcela = (pid) => {
    const atual = propostas[pid]?.forma_pgto?.parcelas || [];
    const proxDias = atual.length ? Math.min(360, (atual[atual.length - 1]?.dias || 30) + 30) : 30;
    setForma(pid, { parcelas: [...atual, { dias: proxDias }] });
  };
  const removeParcela = (pid, idx) => {
    const atual = [...(propostas[pid]?.forma_pgto?.parcelas || [])];
    atual.splice(idx, 1);
    setForma(pid, { parcelas: atual });
  };
  const setParcelaDias = (pid, idx, dias) => {
    const atual = [...(propostas[pid]?.forma_pgto?.parcelas || [])];
    atual[idx] = { dias: Number(dias) || 0 };
    setForma(pid, { parcelas: atual });
  };

  const salvarItem = async (item) => {
    const pid = item.produto_id;
    const p = propostas[pid];
    if (!p) return;
    if (!p.preco || Number(p.preco) <= 0) return toast.error("Informe o preço unitário.");
    if (p.forma_pgto?.tipo === "prazo" && (!p.forma_pgto.parcelas || p.forma_pgto.parcelas.length === 0)) {
      return toast.error("Informe as parcelas do pagamento a prazo.");
    }
    setSavingItem((s) => ({ ...s, [pid]: true }));
    try {
      await axios.post(`${API}/public/cotacao/${token}/proposta`, {
        produto_id: pid,
        preco: Number(p.preco),
        frete: p.frete,
        forma_pgto: p.forma_pgto,
        observacoes: p.observacoes || null,
      });
      setPropostas((s) => ({ ...s, [pid]: { ...s[pid], saved: true } }));
      toast.success(`"${item.nome}" salvo!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSavingItem((s) => ({ ...s, [pid]: false }));
    }
  };

  const todosPreenchidos = useMemo(() => {
    if (!dados?.itens) return false;
    return dados.itens.every((it) => propostas[it.produto_id]?.saved);
  }, [dados, propostas]);

  const enviarProposta = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/public/cotacao/${token}/enviar`);
      setThankyou(true);
      toast.success("Cotação enviada!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar");
    } finally { setSending(false); }
  };

  const reabrir = async () => {
    try {
      await axios.post(`${API}/public/cotacao/${token}/reabrir`);
      setThankyou(false);
      // recarrega proposta atual do fornecedor (mantém valores digitados anteriormente)
      const { data } = await axios.get(`${API}/public/cotacao/${token}`);
      setDados(data);
      const inicial = {};
      (data.itens || []).forEach((it) => {
        if (it.proposta) {
          inicial[it.produto_id] = {
            preco: it.proposta.preco ?? "",
            frete: it.proposta.frete || "CIF",
            forma_pgto: it.proposta.forma_pgto || { tipo: "avista", parcelas: [] },
            observacoes: it.proposta.observacoes || "",
            saved: true,
          };
        } else {
          inicial[it.produto_id] = { ...emptyProposta(), saved: false };
        }
      });
      setPropostas(inicial);
      toast.success("Você pode editar sua proposta novamente.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível reabrir");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando cotação...</div>;
  if (erro) return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <Card className="max-w-md w-full"><CardContent className="p-6 text-center">
        <div className="text-red-600 font-semibold mb-1">Cotação encerrada</div>
        <div className="text-sm text-slate-600">{erro}</div>
      </CardContent></Card>
    </div>
  );

  if (thankyou) return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-emerald-50">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold mb-1">Obrigado por enviar a proposta!</h1>
          <p className="text-slate-600">Sua cotação foi registrada e está <b>aguardando aprovação</b> do solicitante <b>{dados.solicitante}</b>.</p>
          <div className="mt-4 text-xs text-slate-500">Cotação nº {dados.numero}</div>
          <div className="mt-6">
            <Button onClick={reabrir} variant="outline" data-testid="btn-reabrir-proposta">
              Atualizar minha proposta
            </Button>
            <div className="text-[11px] text-slate-500 mt-2">
              Você pode editar até o solicitante fechar a cotação.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="loja-fornecedor-page">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/logo-compromisso.png" alt="CD" className="w-10 h-10 rounded-xl" />
          <div className="flex-1 min-w-0">
            <div className="font-bold" style={{ fontFamily: "Work Sans" }}>Sistema de Cotações</div>
            <div className="text-xs text-slate-500">Loja Digital do fornecedor</div>
          </div>
          <div className="text-right text-xs">
            <div className="text-slate-500">Cotação nº</div>
            <div className="font-semibold">{dados.numero}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4">
        <Card className="mb-4">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-1">
              <FileSpreadsheet className="w-6 h-6 text-blue-700" />
              <h1 className="text-lg font-bold">{dados.titulo || `Cotação nº ${dados.numero}`}</h1>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm">
              <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-500" /> Solicitante: <b>{dados.solicitante}</b></div>
              <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-500" /> Fornecedor: <b>{dados.fornecedor.nome_fantasia || dados.fornecedor.razao_social}</b></div>
            </div>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded text-xs text-blue-900 flex gap-2">
              <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
              <div>Preencha o preço + forma de pagamento + frete de cada item e clique em <b>Salvar</b>. Depois de todos preenchidos, o botão <b>Enviar Cotação</b> será liberado.</div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {dados.itens.map((it) => {
            const p = propostas[it.produto_id] || emptyProposta();
            const total = Number(p.preco || 0) * Number(it.qtd || 0);
            return (
              <Card key={it.produto_id} data-testid={`item-loja-${it.produto_id}`} className={p.saved ? "border-emerald-300" : ""}>
                <CardContent className="p-4">
                  <div className="flex justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">
                        {it.codigo && <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded mr-1">{it.codigo}</span>}
                        {it.nome}
                      </div>
                      <div className="text-xs text-slate-500">
                        {it.marca && <>Marca: {it.marca} · </>}{it.referencia && <>Ref: {it.referencia} · </>}Un: {it.unidade || "un"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Quantidade</div>
                      <div className="font-semibold">{it.qtd} {it.unidade}</div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Preço unitário (R$)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={p.preco}
                        onChange={(e) => setField(it.produto_id, "preco", e.target.value)}
                        data-testid={`preco-${it.produto_id}`}
                        placeholder="0,00"
                      />
                    </div>
                    <div>
                      <Label>Frete</Label>
                      <select
                        value={p.frete}
                        onChange={(e) => setField(it.produto_id, "frete", e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm"
                        data-testid={`frete-${it.produto_id}`}
                      >
                        <option value="CIF">CIF (Fornecedor paga)</option>
                        <option value="FOB">FOB (Cliente paga)</option>
                      </select>
                    </div>
                    <div>
                      <Label>Total do item</Label>
                      <div className="border rounded px-3 py-2 bg-slate-50 text-sm font-semibold text-slate-700">
                        {total > 0 ? brl(total) : "—"}
                      </div>
                    </div>
                  </div>

                  {/* Forma de pagamento */}
                  <div className="mt-3">
                    <Label>Forma de pagamento</Label>
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { v: "avista", l: "À vista" },
                        { v: "boleto", l: "Boleto" },
                        { v: "prazo", l: "Prazo" },
                      ].map((op) => (
                        <button
                          key={op.v} type="button"
                          onClick={() => setForma(it.produto_id, { tipo: op.v, parcelas: op.v === "prazo" ? (p.forma_pgto?.parcelas?.length ? p.forma_pgto.parcelas : [{ dias: 30 }]) : [] })}
                          className={`px-3 py-1.5 rounded-full text-sm border transition ${p.forma_pgto?.tipo === op.v ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}
                          data-testid={`pgto-${op.v}-${it.produto_id}`}
                        >{op.l}</button>
                      ))}
                    </div>
                    {p.forma_pgto?.tipo === "prazo" && (
                      <div className="mt-2 space-y-2">
                        {(p.forma_pgto?.parcelas || []).map((par, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input type="number" min="0" max="365" value={par.dias}
                              onChange={(e) => setParcelaDias(it.produto_id, idx, e.target.value)}
                              className="max-w-[120px]"
                              data-testid={`parcela-${idx}-${it.produto_id}`}
                            />
                            <span className="text-sm text-slate-600">dias</span>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeParcela(it.produto_id, idx)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={() => addParcela(it.produto_id)} data-testid={`add-parcela-${it.produto_id}`}>
                          <PlusCircle className="w-4 h-4 mr-1" /> Nova parcela
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="mt-3">
                    <Label>Observações (opcional)</Label>
                    <Input value={p.observacoes} onChange={(e) => setField(it.produto_id, "observacoes", e.target.value)} placeholder="Ex: entrega em 5 dias úteis" />
                  </div>

                  <div className="mt-3 flex justify-end">
                    <Button
                      onClick={() => salvarItem(it)}
                      disabled={savingItem[it.produto_id]}
                      className={p.saved ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-700 hover:bg-blue-800"}
                      data-testid={`btn-salvar-item-${it.produto_id}`}
                    >
                      {savingItem[it.produto_id] ? "Salvando..." : (p.saved ? <><Check className="w-4 h-4 mr-1" /> Salvo</> : "Salvar item")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Botão enviar */}
        <div className="mt-6 sticky bottom-4">
          <Card>
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm">
                  {dados.itens.filter((it) => propostas[it.produto_id]?.saved).length} de {dados.itens.length} itens preenchidos
                </div>
                {!todosPreenchidos && (
                  <div className="text-xs text-slate-500">Salve cada item para liberar o envio.</div>
                )}
              </div>
              <Button
                onClick={enviarProposta}
                disabled={!todosPreenchidos || sending}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-enviar-cotacao"
              >
                {sending ? "Enviando..." : <>Enviar Cotação <Send className="w-4 h-4 ml-1" /></>}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} Compromisso Digital · Sistema de Cotações
        </div>
      </main>
    </div>
  );
}
